import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Code as CodeIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
  StarOutline as StarOutlineIcon,
  Storage as StorageIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import SideDrawer from '../common/SideDrawer';
import type { ConnectorConfig } from '../../services/api';
import { apiService } from '../../services/api';
import { discoverTables, previewTable } from '../../services/connectorApi';
import type { DiscoveredTable } from '../../services/connectorApi';
import TableBrowser from '../common/TableBrowser';
import type { TableEntry } from '../common/TableBrowser';
import WizardStepSources from '../dashboard/WizardStepSources';
import WizardStepConfigure from '../dashboard/WizardStepConfigure';

/** File Upload tab — must match on-screen copy (SQL, CSV, Excel only). */
const SCHEMA_FILE_UPLOAD_ALLOWED_EXT = new Set(['sql', 'csv', 'xlsx', 'xls']);
const SCHEMA_FILE_UPLOAD_ACCEPT = '.sql,.csv,.xlsx,.xls';

function schemaFileUploadExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

function isAllowedSchemaFileUpload(filename: string): boolean {
  return SCHEMA_FILE_UPLOAD_ALLOWED_EXT.has(schemaFileUploadExtension(filename));
}

interface UploadSchemaDialogProps {
  open: boolean;
  onClose: () => void;
  uploadMode: 'file' | 'sql' | 'json' | 'connector' | 'template';
  onUploadModeChange: (mode: 'file' | 'sql' | 'json' | 'connector' | 'template') => void;
  uploadContent: string;
  onUploadContentChange: (value: string) => void;
  uploadSchemaName: string;
  onUploadSchemaNameChange: (value: string) => void;
  uploadFiles: { name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[];
  onUploadFilesChange: React.Dispatch<React.SetStateAction<{ name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[]>>;
  uploadFileType: 'text' | 'csv' | 'excel' | 'sql';
  onUploadFileTypeChange: (value: 'text' | 'csv' | 'excel' | 'sql') => void;
  selectedConnectorId: string;
  onSelectedConnectorIdChange: (value: string) => void;
  availableTablesForExtract: string[];
  onAvailableTablesChange: (value: string[]) => void;
  selectedTablesForExtract: string[];
  onSelectedTablesChange: (value: string[]) => void;
  discoverLoading: boolean;
  onDiscoverLoadingChange: (value: boolean) => void;
  usableConnectors: ConnectorConfig[];
  templateAdditionalFiles: Record<string, string>;
  onTemplateAdditionalFilesChange: (files: Record<string, string>) => void;
  onUpload: (opts?: { selectedSheets?: string[]; selectedColumnsBySheet?: Record<string, string[]>; fileType?: 'csv' | 'excel' | 'sql'; content?: string }) => void;
  onExtract: () => void;
  isUploading: boolean;
  isExtracting: boolean;
  onSnackbar: (snackbar: { severity: 'success' | 'error'; message: string }) => void;
}

const UploadSchemaDialog: React.FC<UploadSchemaDialogProps> = ({
  open,
  onClose,
  uploadMode,
  onUploadModeChange,
  uploadContent,
  onUploadContentChange,
  uploadSchemaName,
  onUploadSchemaNameChange,
  uploadFiles,
  onUploadFilesChange,
  uploadFileType,
  onUploadFileTypeChange,
  selectedConnectorId,
  onSelectedConnectorIdChange,
  availableTablesForExtract,
  onAvailableTablesChange,
  selectedTablesForExtract,
  onSelectedTablesChange,
  discoverLoading,
  onDiscoverLoadingChange,
  templateAdditionalFiles,
  onTemplateAdditionalFilesChange,
  usableConnectors,
  onUpload,
  onExtract,
  isUploading,
  isExtracting,
  onSnackbar,
}) => {
  const [modeBuffers, setModeBuffers] = useState<Partial<Record<string, string>>>({});
  const [modeSchemaNameBuffers, setModeSchemaNameBuffers] = useState<Partial<Record<string, string>>>({});

  // File wizard state (used when uploadMode === 'file')
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardFiles, setWizardFiles] = useState<File[]>([]);
  const [wizardColumnSelections, setWizardColumnSelections] = useState<Map<string, string[]>>(new Map());
  const [wizardConfigureValid, setWizardConfigureValid] = useState(false);

  useEffect(() => {
    if (!open) {
      setModeBuffers({});
      setModeSchemaNameBuffers({});
      setWizardStep(0);
      setWizardFiles([]);
      setWizardColumnSelections(new Map());
      setWizardConfigureValid(false);
    }
  }, [open]);

  useEffect(() => {
    setWizardStep(0);
    setWizardFiles([]);
    setWizardColumnSelections(new Map());
    setWizardConfigureValid(false);
  }, [uploadMode]);

  const handleModeChange = (nextMode: string) => {
    if (nextMode === uploadMode) return;
    setModeBuffers((prev) => ({ ...prev, [uploadMode]: uploadContent }));
    setModeSchemaNameBuffers((prev) => ({ ...prev, [uploadMode]: uploadSchemaName }));
    onUploadContentChange(modeBuffers[nextMode] ?? '');
    onUploadSchemaNameChange(modeSchemaNameBuffers[nextMode] ?? '');
    onUploadModeChange(nextMode as typeof uploadMode);
  };

  const handleSchemaNameChange = (value: string) => {
    setModeSchemaNameBuffers((prev) => ({ ...prev, [uploadMode]: value }));
    onUploadSchemaNameChange(value);
  };

  // Wrap content change so we also keep the buffer current as the user types
  const handleContentChange = (value: string) => {
    setModeBuffers((prev) => ({ ...prev, [uploadMode]: value }));
    onUploadContentChange(value);
  };

  const handleFileWizardUpload = useCallback(async () => {
    if (wizardFiles.length === 0) return;

    const excelFile = wizardFiles.find((f) => /\.(xlsx|xls)$/i.test(f.name));

    if (excelFile) {
      const prefix = `file::${excelFile.name}::${excelFile.size}::`;
      const sheetEntries = Array.from(wizardColumnSelections.entries())
        .filter(([key]) => key.startsWith(prefix));
      const sheets = sheetEntries
        .filter(([, cols]) => cols.length > 0)
        .map(([key]) => key.slice(prefix.length));

      // Only process the Excel if at least one sheet is selected.
      // If all sheets were deselected, fall through to process non-Excel files.
      if (sheets.length > 0 || sheetEntries.length === 0) {
        const selectedColumnsBySheet: Record<string, string[]> = {};
        sheetEntries.forEach(([key, cols]) => {
          selectedColumnsBySheet[key.slice(prefix.length)] = cols;
        });

        const arrayBuffer = await excelFile.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const base64 = btoa(binary);

        onUploadFilesChange([{ name: excelFile.name, content: base64, type: 'excel' }]);
        onUploadFileTypeChange('excel');
        onUploadContentChange(base64);
        onUpload({
          selectedSheets: sheets.length > 0 ? sheets : undefined,
          selectedColumnsBySheet: Object.keys(selectedColumnsBySheet).length > 0 ? selectedColumnsBySheet : undefined,
          fileType: 'excel',
          content: base64,
        });
        return;
      }
      // All Excel sheets deselected — fall through to process non-Excel files below.
    }

    // CSV or SQL (multiple files, or Excel was fully deselected)
    const nonExcelFiles = wizardFiles.filter((f) => !/\.(xlsx|xls)$/i.test(f.name));
    // Exclude single-file entries where user deselected all columns
    const filesToProcess = nonExcelFiles.filter((f) => {
      const key = `file::${f.name}::${f.size}`;
      const cols = wizardColumnSelections.get(key);
      return !cols || cols.length > 0;
    });
    if (filesToProcess.length === 0) return;

    const hasCsv = filesToProcess.some((f) => /\.csv$/i.test(f.name));
    const type = hasCsv ? 'csv' as const : 'sql' as const;
    const entries = await Promise.all(
      filesToProcess.map(async (f) => ({ name: f.name, content: await f.text(), type })),
    );
    onUploadFilesChange(entries);
    onUploadFileTypeChange(type);
    const content = type === 'csv'
      ? JSON.stringify(Object.fromEntries(entries.map((e) => [e.name, e.content])))
      : entries.map((e) => e.content).join('\n\n');
    onUploadContentChange(content);

    let selectedColumnsBySheet: Record<string, string[]> | undefined;
    if (type === 'csv') {
      const colsByFile: Record<string, string[]> = {};
      filesToProcess.forEach((f) => {
        const key = `file::${f.name}::${f.size}`;
        const cols = wizardColumnSelections.get(key);
        if (cols && cols.length > 0) colsByFile[f.name] = cols;
      });
      if (Object.keys(colsByFile).length > 0) selectedColumnsBySheet = colsByFile;
    }

    onUpload({ fileType: type, content, selectedColumnsBySheet });
  }, [wizardFiles, wizardColumnSelections, onUploadFilesChange, onUploadFileTypeChange, onUploadContentChange, onUpload]);

  const footer = (
    <>
      <Button onClick={onClose}>Cancel</Button>

      {uploadMode === 'file' && wizardStep === 0 && (
        <Button
          variant="contained"
          onClick={() => setWizardStep(1)}
          disabled={wizardFiles.length === 0}
        >
          Next
        </Button>
      )}
      {uploadMode === 'file' && wizardStep === 1 && (
        <>
          <Button onClick={() => setWizardStep(0)} disabled={isUploading}>Back</Button>
          <Button
            variant="contained"
            onClick={handleFileWizardUpload}
            disabled={!wizardConfigureValid || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
          >
            {isUploading ? 'Uploading...' : `Upload & Parse`}
          </Button>
        </>
      )}
      {uploadMode !== 'file' && (
        <Button
          onClick={uploadMode === 'connector' ? onExtract : () => onUpload()}
          variant="contained"
          disabled={
            uploadMode === 'connector'
              ? isExtracting || !selectedConnectorId
              : isUploading || !uploadContent.trim()
          }
        >
          {uploadMode === 'connector' ? (
            isExtracting ? <CircularProgress size={24} /> : 'Extract Schema'
          ) : isUploading ? (
            <CircularProgress size={24} />
          ) : (
            'Upload & Parse'
          )}
        </Button>
      )}
    </>
  );

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title="Upload Schema"
      footer={footer}
      defaultWidth="min(72vw, 960px)"
    >
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={2}>
          <Tabs
            value={uploadMode}
            onChange={(_, v) => handleModeChange(v)}
          >
            <Tab label="SQL DDL" value="sql" icon={<CodeIcon />} iconPosition="start" />
            {/* <Tab label="JSON Template" value="json" icon={<DescriptionIcon />} iconPosition="start" /> */}
            <Tab label="File Upload" value="file" icon={<UploadIcon />} iconPosition="start" />
            <Tab label="From Connector" value="connector" icon={<StorageIcon />} iconPosition="start" />
            <Tab label="Template" value="template" icon={<DescriptionIcon />} iconPosition="start" />
          </Tabs>

          <TextField
            label="Schema Name (Optional)"
            fullWidth
            size="small"
            value={uploadSchemaName}
            onChange={(e) => handleSchemaNameChange(e.target.value)}
            placeholder="e.g., CustomerSchema, SalesDatabase"
          />

          {uploadMode === 'sql' && (
            <SqlDdlSection
              uploadContent={uploadContent}
              onUploadContentChange={handleContentChange}
            />
          )}

          {uploadMode === 'json' && (
            <>
              <Typography variant="body2" color="text.secondary">
                Paste JSON schema definition. Supports various formats including JSON Schema.
              </Typography>
              <TextField
                multiline
                rows={16}
                fullWidth
                value={uploadContent}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder={`{\n  "tables": {\n    "customers": {\n      "description": "Customer information",\n      "columns": {\n        "customer_id": { "type": "INT", "primary_key": true },\n        "email": { "type": "VARCHAR", "nullable": false },\n        "first_name": { "type": "VARCHAR" }\n      }\n    }\n  }\n}`}
                sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
            </>
          )}

          {uploadMode === 'file' && (
            <>
              <Stepper activeStep={wizardStep} sx={{ mb: 2 }}>
                <Step><StepLabel>Select Files</StepLabel></Step>
                <Step><StepLabel>Configure</StepLabel></Step>
              </Stepper>
              {wizardStep === 0 && (
                <WizardStepSources
                  projectId={null}
                  files={wizardFiles}
                  connectorSources={[]}
                  onFilesChange={setWizardFiles}
                  onConnectorSourcesChange={() => {}}
                  filesOnly
                  accept=".csv,.xlsx,.xls"
                  acceptLabel="CSV, Excel (.xlsx)"
                />
              )}
              {wizardStep === 1 && (
                <WizardStepConfigure
                  files={wizardFiles}
                  connectorSources={[]}
                  columnSelections={wizardColumnSelections}
                  onColumnSelectionsChange={setWizardColumnSelections}
                  onValidityChange={setWizardConfigureValid}
                />
              )}
            </>
          )}

          {uploadMode === 'connector' && (
            <ConnectorExtractSection
              selectedConnectorId={selectedConnectorId}
              onSelectedConnectorIdChange={onSelectedConnectorIdChange}
              availableTablesForExtract={availableTablesForExtract}
              onAvailableTablesChange={onAvailableTablesChange}
              selectedTablesForExtract={selectedTablesForExtract}
              onSelectedTablesChange={onSelectedTablesChange}
              discoverLoading={discoverLoading}
              onDiscoverLoadingChange={onDiscoverLoadingChange}
              usableConnectors={usableConnectors}
              onSnackbar={onSnackbar}
            />
          )}

          {uploadMode === 'template' && (
            <TemplateUploadSection
              uploadContent={uploadContent}
              onUploadContentChange={handleContentChange}
              uploadSchemaName={uploadSchemaName}
              onUploadSchemaNameChange={handleSchemaNameChange}
              templateAdditionalFiles={templateAdditionalFiles}
              onTemplateAdditionalFilesChange={onTemplateAdditionalFilesChange}
            />
          )}
        </Stack>
      </Box>
    </SideDrawer>
  );
};

// ─── Helper: file type chip colour ───────────────────────────────────────────
function fileTypeColor(type: string): 'primary' | 'secondary' | 'success' | 'default' {
  if (type === 'csv') return 'primary';
  if (type === 'sql') return 'secondary';
  if (type === 'excel') return 'success';
  return 'default';
}

// ─── FilePreviewListAndEditor ──────────────────────────────────────────────────
interface FilePreviewItem {
  name: string;
  content: string;
  type?: 'csv' | 'sql' | 'excel' | 'text';
  isMain?: boolean;
}

interface FilePreviewListAndEditorProps {
  files: FilePreviewItem[];
  selectedFile: string | null;
  onSelect: (name: string) => void;
  onDelete: (name: string, index: number) => void;
  onContentChange: (name: string, value: string) => void;
  renderItemActions?: (file: FilePreviewItem) => React.ReactNode;
  placeholderText?: string;
  editorLabel?: string | ((file: FilePreviewItem) => string);
}

const FilePreviewListAndEditor: React.FC<FilePreviewListAndEditorProps> = ({
  files,
  selectedFile,
  onSelect,
  onDelete,
  onContentChange,
  renderItemActions,
  placeholderText = 'Select a file to preview',
  editorLabel,
}) => {
  const selectedFileObj = selectedFile ? files.find((f) => f.name === selectedFile) : null;

  return (
    <Stack direction="row" spacing={2} sx={{ minHeight: 320 }}>
      {/* Left: file list */}
      <Paper
        variant="outlined"
        sx={{ width: 220, flexShrink: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            FILES
          </Typography>
        </Box>
        <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
          {files.map((f, idx) => (
            <React.Fragment key={`${f.name}-${idx}`}>
              <ListItemButton
                selected={selectedFile === f.name}
                onClick={() => onSelect(f.name)}
                sx={{ py: 0.75 }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ flex: 1, fontSize: '0.8rem' }}
                        title={f.name}
                      >
                        {f.name}
                      </Typography>
                      {f.type && (
                        <Chip
                          label={f.type.toUpperCase()}
                          size="small"
                          color={fileTypeColor(f.type)}
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                    </Stack>
                  }
                />
                <Stack direction="row" alignItems="center">
                  {renderItemActions && renderItemActions(f)}
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onDelete(f.name, idx);
                      }}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemButton>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Right: content preview / editor */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {selectedFileObj ? (
          selectedFileObj.type === 'excel' ? (
            <Paper
              variant="outlined"
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Binary Excel file — preview not available.
              </Typography>
              <Chip label="Excel" color="success" size="small" variant="outlined" />
            </Paper>
          ) : (
            <TextField
              multiline
              fullWidth
              value={selectedFileObj.content}
              onChange={(e) => onContentChange(selectedFileObj.name, e.target.value)}
              label={
                typeof editorLabel === 'function'
                  ? editorLabel(selectedFileObj)
                  : editorLabel || selectedFileObj.name
              }
              sx={{ fontFamily: 'monospace', fontSize: '0.875rem', flex: 1 }}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
              InputProps={{ sx: { alignItems: 'flex-start' } }}
              minRows={14}
            />
          )
        ) : (
          <Paper variant="outlined" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {placeholderText}
            </Typography>
          </Paper>
        )}
      </Box>
    </Stack>
  );
};

// ─── SqlDdlSection ───────────────────────────────────────────────────────────
interface SqlDdlSectionProps {
  uploadContent: string;
  onUploadContentChange: (value: string) => void;
}

const SqlDdlSection: React.FC<SqlDdlSectionProps> = ({
  uploadContent,
  onUploadContentChange,
}) => {
  // Local list of uploaded SQL files for the sidebar
  const [sqlFiles, setSqlFiles] = useState<{ name: string; content: string }[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  // When a file is selected in the sidebar, show its content in the editor
  const handleSelect = (name: string) => {
    setSelectedFile(name);
    const file = sqlFiles.find((f) => f.name === name);
    if (file) onUploadContentChange(file.content);
  };

  // Edit content and keep the sqlFiles list in sync
  const handleContentChange = (name: string, value: string) => {
    onUploadContentChange(value);
    if (selectedFile) {
      setSqlFiles((prev) =>
        prev.map((f) => (f.name === selectedFile ? { ...f, content: value } : f)),
      );
    }
  };

  const handleDelete = (name: string) => {
    setSqlFiles((prev) => {
      const updated = prev.filter((f) => f.name !== name);
      if (selectedFile === name) {
        if (updated.length > 0) {
          setSelectedFile(updated[0].name);
          onUploadContentChange(updated[0].content);
        } else {
          setSelectedFile(null);
          onUploadContentChange('');
        }
      } else if (updated.length > 0) {
        // recompute joined content
        onUploadContentChange(updated.map((f) => f.content).join('\n\n'));
      }
      return updated;
    });
  };

  const handleFilesAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let pending = files.length;
    const entries: { name: string; content: string }[] = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        entries.push({ name: file.name, content: evt.target?.result as string });
        pending--;
        if (pending === 0) {
          setSqlFiles((prev) => {
            const combined = [...prev, ...entries];
            onUploadContentChange(combined.map((f) => f.content).join('\n\n'));
            return combined;
          });
          // Select first newly-added file
          setSelectedFile(entries[0].name);
          onUploadContentChange(entries[0].content);
        }
      };
      reader.readAsText(file);
    });
    e.target.value = '';
  };

  const hasFiles = sqlFiles.length > 0;

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        Paste SQL CREATE TABLE statements below, or upload one or more <code>.sql</code> files.
        The system will parse tables, columns, and constraints.
      </Typography>
      <Button variant="outlined" component="label" fullWidth>
        {hasFiles ? `${sqlFiles.length} file(s) loaded — Add more` : 'Upload SQL File(s) (optional)'}
        <input
          type="file"
          hidden
          multiple
          accept=".sql"
          onChange={handleFilesAdded}
        />
      </Button>

      {hasFiles ? (
        <FilePreviewListAndEditor
          files={sqlFiles.map((f) => ({ name: f.name, content: f.content, type: 'sql' }))}
          selectedFile={selectedFile}
          onSelect={handleSelect}
          onDelete={(name) => handleDelete(name)}
          onContentChange={handleContentChange}
          placeholderText="Select an SQL file to preview"
          editorLabel={(f) => f.name}
        />
      ) : (
        /* No files uploaded — plain paste editor */
        <TextField
          multiline
          rows={16}
          fullWidth
          value={uploadContent}
          onChange={(e) => onUploadContentChange(e.target.value)}
          placeholder={`CREATE TABLE customers (\n  customer_id INT PRIMARY KEY AUTO_INCREMENT,\n  email VARCHAR(255) NOT NULL UNIQUE,\n  first_name VARCHAR(100),\n  last_name VARCHAR(100),\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);\n\nCREATE TABLE orders (\n  order_id INT PRIMARY KEY,\n  customer_id INT NOT NULL,\n  order_date DATE,\n  total_amount DECIMAL(10,2),\n  FOREIGN KEY (customer_id) REFERENCES customers(customer_id)\n);`}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
        />
      )}
    </>
  );
};

// ─── TemplateUploadSection ────────────────────────────────────────────────────
interface TemplateUploadSectionProps {
  uploadContent: string;
  onUploadContentChange: (value: string) => void;
  uploadSchemaName: string;
  onUploadSchemaNameChange: (value: string) => void;
  templateAdditionalFiles: Record<string, string>;
  onTemplateAdditionalFilesChange: (files: Record<string, string>) => void;
}

const TemplateUploadSection: React.FC<TemplateUploadSectionProps> = ({
  uploadContent,
  onUploadContentChange,
  uploadSchemaName,
  onUploadSchemaNameChange,
  templateAdditionalFiles,
  onTemplateAdditionalFilesChange,
}) => {
  // Name of the currently selected file in the preview panel
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  // Name we track for the main schema file
  const [mainFileName, setMainFileName] = useState<string | null>(null);

  // Build a combined list: main + supporting files
  const allFiles: { name: string; isMain: boolean; content: string }[] = [
    ...(uploadContent.trim()
      ? [{ name: mainFileName || 'main_schema', isMain: true, content: uploadContent }]
      : []),
    ...Object.entries(templateAdditionalFiles).map(([n, c]) => ({
      name: n,
      isMain: false,
      content: c,
    })),
  ];

  // Auto-select first file when files are added
  useEffect(() => {
    if (allFiles.length > 0 && !selectedFile) {
      setSelectedFile(allFiles[0].name);
    }
    if (allFiles.length === 0) setSelectedFile(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFiles.length]);

  const setContent = (name: string, value: string) => {
    if (name === mainFileName || (mainFileName === null && name === 'main_schema')) {
      onUploadContentChange(value);
    } else {
      onTemplateAdditionalFilesChange({ ...templateAdditionalFiles, [name]: value });
    }
  };

  const handleDelete = (name: string, isMain: boolean) => {
    if (isMain) {
      // Promote first supporting file to main, if any
      const supportingKeys = Object.keys(templateAdditionalFiles);
      if (supportingKeys.length > 0) {
        const [promote, ...rest] = supportingKeys;
        onUploadContentChange(templateAdditionalFiles[promote]);
        onUploadSchemaNameChange(promote.replace(/\.[^.]+$/, ''));
        setMainFileName(promote);
        const next: Record<string, string> = {};
        rest.forEach((k) => { next[k] = templateAdditionalFiles[k]; });
        onTemplateAdditionalFilesChange(next);
        setSelectedFile(promote);
      } else {
        onUploadContentChange('');
        setMainFileName(null);
        setSelectedFile(null);
      }
    } else {
      const next = { ...templateAdditionalFiles };
      delete next[name];
      onTemplateAdditionalFilesChange(next);
      if (selectedFile === name) {
        // Fall back to main
        setSelectedFile(mainFileName || 'main_schema');
      }
    }
  };

  const handleSetAsMain = (name: string) => {
    // Swap: current main → supporting, selected supporting → main
    const currentMainContent = uploadContent;
    const currentMainName = mainFileName || 'main_schema';
    const newMainContent = templateAdditionalFiles[name];

    const next = { ...templateAdditionalFiles };
    delete next[name];
    if (currentMainContent.trim()) {
      next[currentMainName] = currentMainContent;
    }

    onUploadContentChange(newMainContent);
    onUploadSchemaNameChange(name.replace(/\.[^.]+$/, ''));
    setMainFileName(name);
    onTemplateAdditionalFilesChange(next);
    setSelectedFile(name);
  };

  const handleFilesAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    let pending = files.length;
    const entries: { name: string; content: string }[] = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        entries.push({ name: file.name, content: evt.target?.result as string });
        pending--;
        if (pending === 0) {
          // Determine which entry is the main schema
          let mainIdx = 0;
          for (let i = 0; i < entries.length; i++) {
            if (entries[i].content.includes('xs:import') || entries[i].content.includes('xs:redefine')) {
              mainIdx = i;
              break;
            }
          }

          const hasExistingMain = uploadContent.trim();
          if (!hasExistingMain) {
            // No main yet: promote the detected main entry
            onUploadContentChange(entries[mainIdx].content);
            if (!uploadSchemaName) {
              onUploadSchemaNameChange(entries[mainIdx].name.replace(/\.[^.]+$/, ''));
            }
            setMainFileName(entries[mainIdx].name);
            setSelectedFile(entries[mainIdx].name);
          } else {
            mainIdx = -1; // treat all as supporting
            setSelectedFile(entries[0].name);
          }

          const additional: Record<string, string> = { ...templateAdditionalFiles };
          entries.forEach((entry, idx) => {
            if (idx !== mainIdx) {
              additional[entry.name] = entry.content;
            }
          });
          onTemplateAdditionalFilesChange(additional);
        }
      };
      reader.readAsText(file);
    });
    e.target.value = '';
  };

  const hasFiles = allFiles.length > 0;

  return (
    <>
      <Alert severity="info">
        Upload XSD, XML, or JSON template files. For XSD schemas with imports/includes,
        upload all referenced files together — the parser resolves cross-file references
        to extract concrete types, enumerations, and constraints.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        Select one or more files. The main schema is auto-detected (the one with imports).
        Supporting files provide type definitions, enumerations, and constraints.
      </Typography>
      <Button variant="outlined" component="label" fullWidth>
        {hasFiles
          ? `${allFiles.length} file(s) loaded — Add more`
          : 'Choose Template Files (.xml, .xsd, .json)'}
        <input
          type="file"
          hidden
          multiple
          accept=".xml,.xsd,.json"
          onChange={handleFilesAdded}
        />
      </Button>

      {hasFiles && (
        <FilePreviewListAndEditor
          files={allFiles.map((f) => ({
            name: f.name,
            content: f.content,
            isMain: f.isMain,
          }))}
          selectedFile={selectedFile}
          onSelect={setSelectedFile}
          onDelete={(name) =>
            handleDelete(name, allFiles.find((f) => f.name === name)?.isMain || false)
          }
          onContentChange={(name, value) => setContent(name, value)}
          editorLabel={(f) => (f.isMain ? 'Main schema content' : `${f.name} (supporting)`)}
          renderItemActions={(f) => (
            <>
              {!f.isMain && (
                <Tooltip title="Set as main schema">
                  <IconButton
                    size="small"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      handleSetAsMain(f.name);
                    }}
                  >
                    <StarOutlineIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
              {f.isMain && (
                <Tooltip title="Main schema">
                  <StarIcon fontSize="inherit" color="primary" />
                </Tooltip>
              )}
            </>
          )}
        />
      )}
    </>
  );
};

// ─── SheetSelectionStep ──────────────────────────────────────────────────────
interface SheetSelectionStepProps {
  sheetNames: string[];
  selectedSheets: string[];
  onSelectedSheetsChange: (sheets: string[]) => void;
}

const SheetSelectionStep: React.FC<SheetSelectionStepProps> = ({
  sheetNames,
  selectedSheets,
  onSelectedSheetsChange,
}) => {
  const allSelected = selectedSheets.length === sheetNames.length;

  const toggleAll = () => {
    onSelectedSheetsChange(allSelected ? [] : [...sheetNames]);
  };

  const toggle = (name: string) => {
    onSelectedSheetsChange(
      selectedSheets.includes(name)
        ? selectedSheets.filter((s) => s !== name)
        : [...selectedSheets, name],
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Select which Excel sheets to import as tables.
      </Typography>
      <Button size="small" onClick={toggleAll} sx={{ mb: 1 }}>
        {allSelected ? 'Deselect All' : `Select All (${sheetNames.length})`}
      </Button>
      <FormGroup>
        {sheetNames.map((name) => (
          <FormControlLabel
            key={name}
            control={
              <Checkbox
                checked={selectedSheets.includes(name)}
                onChange={() => toggle(name)}
                size="small"
              />
            }
            label={name}
          />
        ))}
      </FormGroup>
      {selectedSheets.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>Please select at least one sheet.</Alert>
      )}
    </Box>
  );
};

// ─── FileUploadSection ────────────────────────────────────────────────────────
interface FileUploadSectionProps {
  uploadFiles: { name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[];
  onUploadFilesChange: React.Dispatch<React.SetStateAction<{ name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[]>>;
  uploadFileType: 'text' | 'csv' | 'excel' | 'sql';
  onUploadFileTypeChange: (value: 'text' | 'csv' | 'excel' | 'sql') => void;
  onUploadContentChange: (value: string) => void;
  onSnackbar: (snackbar: { severity: 'success' | 'error'; message: string }) => void;
  onExcelSheetNamesChange?: (sheets: string[]) => void;
  onSelectedSheetsChange?: (sheets: string[]) => void;
}

/** Parse sheet names from an Excel ArrayBuffer by reading xl/workbook.xml inside the ZIP. */
function extractExcelSheetNames(arrayBuffer: ArrayBuffer): string[] {
  try {
    const bytes = new Uint8Array(arrayBuffer);
    // Find the xl/workbook.xml entry in the ZIP by scanning for its local file header
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const text = decoder.decode(bytes);
    // Extract sheet names from workbook XML embedded in ZIP bytes
    const sheetMatches = Array.from(text.matchAll(/name="([^"]+)"\s+sheetId=/g));
    if (sheetMatches.length > 0) return sheetMatches.map((m) => m[1]);
    // Fallback: look for <sheet name="..." pattern
    const fallback = Array.from(text.matchAll(/<sheet[^>]+name="([^"]+)"/g));
    return fallback.map((m) => m[1]);
  } catch {
    return [];
  }
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  uploadFiles,
  onUploadFilesChange,
  uploadFileType,
  onUploadFileTypeChange,
  onUploadContentChange,
  onSnackbar,
  onExcelSheetNamesChange,
  onSelectedSheetsChange,
}) => {
  const recomputeContent = (files: { name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[]) => {
    const types = new Set(files.map((f) => f.type));
    if (types.has('excel')) onUploadFileTypeChange('excel');
    else if (types.has('csv')) onUploadFileTypeChange('csv');
    else if (types.has('sql')) onUploadFileTypeChange('sql');
    else onUploadFileTypeChange('text');

    if (types.has('csv') && !types.has('excel')) {
      const csvMap: Record<string, string> = {};
      files.filter((f) => f.type === 'csv').forEach((f) => { csvMap[f.name] = f.content; });
      onUploadContentChange(JSON.stringify(csvMap));
    } else if (types.has('excel')) {
      const excelFile = files.filter((f) => f.type === 'excel').pop();
      onUploadContentChange(excelFile?.content || '');
    } else {
      onUploadContentChange(files.map((f) => f.content).join('\n\n'));
    }
  };

  const finalizeFiles = (
    entries: { name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[],
  ) => {
    onUploadFilesChange((prev) => {
      const combined = [...prev, ...entries];
      recomputeContent(combined);
      return combined;
    });
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        Upload SQL, CSV, or Excel files. You can select multiple SQL or CSV files
        (each CSV becomes a separate table). Excel worksheets are treated as individual tables.
      </Typography>
      <Button variant="outlined" component="label" fullWidth>
        {uploadFiles.length > 0 ? `${uploadFiles.length} file(s) selected — Add more` : 'Choose Files'}
        <input
          type="file"
          hidden
          multiple
          accept={SCHEMA_FILE_UPLOAD_ACCEPT}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (!files.length) return;

            const rejected = files.filter((f) => !isAllowedSchemaFileUpload(f.name));
            if (rejected.length) {
              onSnackbar({
                severity: 'error',
                message:
                  `Unsupported file type: ${rejected.map((f) => f.name).join(', ')}. `
                  + 'File upload supports SQL (.sql), CSV (.csv), and Excel (.xlsx, .xls) only.',
              });
            }
            const allowed = files.filter((f) => isAllowedSchemaFileUpload(f.name));
            if (!allowed.length) {
              e.target.value = '';
              return;
            }

            let pendingCount = allowed.length;
            const newEntries: { name: string; content: string; type: 'csv' | 'sql' | 'excel' | 'text' }[] = [];

            allowed.forEach((file) => {
              const ext = schemaFileUploadExtension(file.name);

              if (ext === 'xlsx' || ext === 'xls') {
                const reader = new FileReader();
                reader.onload = (event) => {
                  const arrayBuffer = event.target?.result as ArrayBuffer;
                  const bytes = new Uint8Array(arrayBuffer);
                  let binary = '';
                  for (let i = 0; i < bytes.length; i++) {
                    binary += String.fromCharCode(bytes[i]);
                  }
                  newEntries.push({ name: file.name, content: btoa(binary), type: 'excel' });
                  // Extract sheet names and notify parent
                  const sheets = extractExcelSheetNames(arrayBuffer);
                  if (sheets.length > 0) {
                    onExcelSheetNamesChange?.(sheets);
                    onSelectedSheetsChange?.(sheets);
                  }
                  pendingCount--;
                  if (pendingCount === 0) finalizeFiles(newEntries);
                };
                reader.readAsArrayBuffer(file);
              } else {
                const ftype = ext === 'csv' ? 'csv' as const : 'sql' as const;
                const reader = new FileReader();
                reader.onload = (event) => {
                  newEntries.push({ name: file.name, content: event.target?.result as string, type: ftype });
                  pendingCount--;
                  if (pendingCount === 0) finalizeFiles(newEntries);
                };
                reader.readAsText(file);
              }
            });

            e.target.value = '';
          }}
        />
      </Button>

      {uploadFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {uploadFiles.map((f, idx) => (
            <Chip
              key={`${f.name}-${idx}`}
              label={f.name}
              size="small"
              color={fileTypeColor(f.type)}
              variant="outlined"
              onDelete={() => {
                onUploadFilesChange((prev) => {
                  const updated = prev.filter((_, i) => i !== idx);
                  if (updated.length === 0) {
                    onUploadContentChange('');
                    onUploadFileTypeChange('text');
                    return [];
                  }
                  recomputeContent(updated);
                  return updated;
                });
              }}
            />
          ))}
        </Box>
      )}

      {uploadFiles.length > 0 && (
        <Alert severity="info">
          {uploadFileType === 'csv'
            ? `${uploadFiles.filter((f) => f.type === 'csv').length} CSV file(s) — each will become a table`
            : uploadFileType === 'sql'
              ? `${uploadFiles.filter((f) => f.type === 'sql').length} SQL file(s) — statements will be combined`
              : uploadFileType === 'excel'
                ? `Excel file loaded: ${uploadFiles.find((f) => f.type === 'excel')?.name} (each worksheet = table)`
                : `${uploadFiles.length} file(s) loaded`}
        </Alert>
      )}
    </>
  );
};

interface ConnectorExtractSectionProps {
  selectedConnectorId: string;
  onSelectedConnectorIdChange: (value: string) => void;
  availableTablesForExtract: string[];
  onAvailableTablesChange: (value: string[]) => void;
  selectedTablesForExtract: string[];
  onSelectedTablesChange: (value: string[]) => void;
  discoverLoading: boolean;
  onDiscoverLoadingChange: (value: boolean) => void;
  usableConnectors: ConnectorConfig[];
  onSnackbar: (snackbar: { severity: 'success' | 'error'; message: string }) => void;
}

const PAGE_SIZE = 20;

const ConnectorExtractSection: React.FC<ConnectorExtractSectionProps> = ({
  selectedConnectorId,
  onSelectedConnectorIdChange,
  availableTablesForExtract,
  onAvailableTablesChange,
  selectedTablesForExtract,
  onSelectedTablesChange,
  discoverLoading,
  onDiscoverLoadingChange,
  usableConnectors,
  onSnackbar,
}) => {
  // Internal TableBrowser state
  const [tables, setTables] = useState<TableEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [selectedColumns, setSelectedColumns] = useState<Map<string, string[]>>(new Map());
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Sync internal Set<string> → parent string[]
  const handleSelectionChange = useCallback(
    (selected: Set<string>) => {
      setSelectedTables(selected);
      onSelectedTablesChange(Array.from(selected));
    },
    [onSelectedTablesChange],
  );

  // Fetch tables from connector
  const fetchTables = useCallback(
    async (connectorId: string, searchStr: string, pageNum: number) => {
      onDiscoverLoadingChange(true);
      try {
        const resp = await discoverTables({
          connectorId,
          search: searchStr || undefined,
          includeColumns: false,
          page: pageNum,
          pageSize: PAGE_SIZE,
        });
        const entries: TableEntry[] = (resp.tables || []).map(
          (t: DiscoveredTable): TableEntry => ({
            name: t.name,
            schemaName: t.schema_name || undefined,
            rowCountEstimate: t.row_count_estimate ?? undefined,
            columnCount: t.column_count ?? undefined,
          }),
        );
        setTables(entries);
        setTotalCount(resp.total_count);
        // Also sync the flat table names to parent for backward compat
        const allNames = (resp.tables || []).map((t: DiscoveredTable) => t.name);
        onAvailableTablesChange(allNames);
      } catch {
        setTables([]);
        setTotalCount(0);
        onSnackbar({ severity: 'error', message: 'Failed to discover tables.' });
      } finally {
        onDiscoverLoadingChange(false);
      }
    },
    [onDiscoverLoadingChange, onAvailableTablesChange, onSnackbar],
  );

  // Auto-discover when connector changes
  useEffect(() => {
    if (!selectedConnectorId) {
      setTables([]);
      setTotalCount(0);
      setSearch('');
      setPage(1);
      setSelectedTables(new Set());
      setSelectedColumns(new Map());
      return;
    }
    setSearch('');
    setPage(1);
    setSelectedTables(new Set());
    setSelectedColumns(new Map());
    fetchTables(selectedConnectorId, '', 1);
  }, [selectedConnectorId, fetchTables]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        if (selectedConnectorId) {
          setPage(1);
          fetchTables(selectedConnectorId, value, 1);
        }
      }, 300);
    },
    [selectedConnectorId, fetchTables],
  );

  // Page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      if (selectedConnectorId) {
        fetchTables(selectedConnectorId, search, newPage);
      }
    },
    [selectedConnectorId, search, fetchTables],
  );

  // Expand table → fetch columns
  const handleExpandTable = useCallback(
    async (tableName: string) => {
      if (!selectedConnectorId) return;
      setTables((prev) =>
        prev.map((t) => (t.name === tableName ? { ...t, columnsLoading: true } : t)),
      );
      try {
        const resp = await discoverTables({
          connectorId: selectedConnectorId,
          search: tableName,
          includeColumns: true,
          page: 1,
          pageSize: 1,
        });
        const match = resp.tables?.find((t: DiscoveredTable) => t.name === tableName);
        if (match?.columns) {
          setTables((prev) =>
            prev.map((t) =>
              t.name === tableName
                ? {
                    ...t,
                    columns: match.columns!.map((c) => ({
                      name: c.name,
                      dtype: c.dtype,
                      nullable: c.nullable,
                    })),
                    columnCount: match.columns!.length,
                    columnsLoading: false,
                  }
                : t,
            ),
          );
          // Initialize column selection with all columns
          setSelectedColumns((prev) => {
            const next = new Map(prev);
            if (!next.has(tableName)) {
              next.set(tableName, match.columns!.map((c) => c.name));
            }
            return next;
          });
        } else {
          setTables((prev) =>
            prev.map((t) =>
              t.name === tableName ? { ...t, columns: [], columnsLoading: false } : t,
            ),
          );
        }
      } catch {
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName ? { ...t, columns: [], columnsLoading: false } : t,
          ),
        );
      }
    },
    [selectedConnectorId],
  );

  // Preview table
  const handlePreviewTable = useCallback(
    async (tableName: string) => {
      if (!selectedConnectorId) return;
      setTables((prev) =>
        prev.map((t) => (t.name === tableName ? { ...t, previewLoading: true } : t)),
      );
      try {
        const resp = await previewTable({
          connectorId: selectedConnectorId,
          tableName,
          limit: 10,
          columns: selectedColumns.get(tableName),
        });
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName
              ? { ...t, previewRows: resp.rows, previewLoading: false }
              : t,
          ),
        );
      } catch {
        setTables((prev) =>
          prev.map((t) =>
            t.name === tableName ? { ...t, previewRows: [], previewLoading: false } : t,
          ),
        );
      }
    },
    [selectedConnectorId, selectedColumns],
  );

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        Extract schema directly from a connected data source. SQL databases provide rich metadata
        including column types, primary keys, and foreign keys.
      </Typography>

      <TextField
        select
        label="Select Connector"
        fullWidth
        size="small"
        value={selectedConnectorId}
        onChange={(e) => {
          onSelectedConnectorIdChange(e.target.value);
          onAvailableTablesChange([]);
          onSelectedTablesChange([]);
        }}
      >
        <MenuItem value="" disabled>
          Choose a saved connector...
        </MenuItem>
        {usableConnectors.map((conn) => (
          <MenuItem key={conn.connector_id} value={conn.connector_id}>
            {conn.name} ({conn.connector_type})
          </MenuItem>
        ))}
      </TextField>

      {selectedConnectorId && tables.length === 0 && !discoverLoading && (
        <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2 }}>
          Discovering tables...
        </Typography>
      )}

      {(tables.length > 0 || discoverLoading) && (
        <Box>
          <TableBrowser
            tables={tables}
            loading={discoverLoading}
            totalCount={totalCount}
            searchValue={search}
            onSearchChange={handleSearchChange}
            selectionMode="multi"
            selectedTables={selectedTables}
            onSelectionChange={handleSelectionChange}
            columnSelectionEnabled
            selectedColumns={selectedColumns}
            onColumnSelectionChange={(table, cols) => {
              setSelectedColumns((prev) => {
                const next = new Map(prev);
                next.set(table, cols);
                return next;
              });
            }}
            onExpandTable={handleExpandTable}
            onPreviewTable={handlePreviewTable}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </>
  );
};

export default UploadSchemaDialog;
